// Opening a link that leaves the app.
//
// Every call site used to be a bare `window.open(url, "_blank")`, which fails in three ways:
//
//  1. Links typed by a user (tutor session reports, project links) routinely arrive without a
//     scheme — "drive.google.com/...". `window.open` treats those as RELATIVE and resolves them
//     against the app origin, so the user lands on a blank SPA route instead of the link.
//  2. Inside the Capacitor WebView `window.open("_blank")` is unreliable — Android WebView drops
//     it unless multi-window support is enabled — so on device we hand off to @capacitor/browser.
//  3. A blocked pop-up (or an empty link) fails silently, which reads to the user as "the button
//     is broken".
//
// The normalisation half is a pure function so it can also gate what gets SAVED (see the yup
// link schema in ModalTutorSessionLog and the inline editor in TutoringTutorSessionReportCard) —
// fixing the value at the source, not only at the point of opening it.

import { showCustomMessage } from "@/methods/utils";
import { AppLauncher } from "@capacitor/app-launcher";
import { Browser } from "@capacitor/browser";
import { Capacitor } from "@capacitor/core";

// "https://host/x" — a scheme followed by "//". Deliberately NOT `scheme:` alone, or a host with
// a port ("example.com:8080/x") would read as a scheme named "example.com" and be thrown away.
const NETWORK_SCHEME_PATTERN = /^[a-z][a-z0-9+.-]*:\/\//i;
// "javascript:", "data:", "mailto:", "tel:" — a scheme with no "//" and no dot in its name, so a
// dotted host with a port never matches.
const OPAQUE_SCHEME_PATTERN = /^[a-z][a-z0-9+-]*:/i;

const MISSING_LINK_MESSAGE = "No link available to open.";
const BLOCKED_LINK_MESSAGE = "The link could not be opened. Please allow pop-ups and try again.";
const UNOPENABLE_LINK_MESSAGE = "The link could not be opened.";

/**
 * Pure normaliser: returns a safe, absolute http(s) URL, or null when the value cannot be one.
 *
 * - A missing scheme is assumed to be https, which is what a user pasting "drive.google.com/x"
 *   means. Protocol-relative "//host/x" is treated the same way.
 * - Anything that is not http(s) after that — javascript:, data:, mailto: — is rejected rather
 *   than opened: these links come from user input and are rendered for OTHER users.
 * - The original string is returned untouched (apart from the scheme and trimming) so a saved
 *   link is never silently re-encoded by the URL parser.
 */
export function normalizeExternalUrl(url: string | null | undefined): string | null {
  const trimmedUrl = url?.trim();
  if (!trimmedUrl) {
    return null;
  }
  const isNetworkUrl = NETWORK_SCHEME_PATTERN.test(trimmedUrl);
  if (!isNetworkUrl && OPAQUE_SCHEME_PATTERN.test(trimmedUrl)) {
    return null;
  }
  const candidateUrl = isNetworkUrl ? trimmedUrl : `https://${trimmedUrl.replace(/^\/+/, "")}`;

  let parsedUrl: URL;
  try {
    parsedUrl = new URL(candidateUrl);
  } catch {
    return null;
  }
  if (parsedUrl.protocol !== "http:" && parsedUrl.protocol !== "https:") {
    return null;
  }
  // Only GUESSED links have to look like a public host: "Report pending" already fails to parse,
  // but a single word like "pending" would otherwise become a valid host. A caller that wrote the
  // scheme itself is trusted, so http://localhost:5173/... and intranet names still work.
  if (!isNetworkUrl && !parsedUrl.hostname.includes(".")) {
    return null;
  }
  return candidateUrl;
}

/** True when the value is empty or already a link this app is willing to open. */
export function isValidExternalUrl(url: string | null | undefined): boolean {
  return !url?.trim() || normalizeExternalUrl(url) !== null;
}

export interface ExternalLinkOptions {
  /**
   * Hand the link to the OS instead of the in-app browser. Set it for links the OS owns —
   * an app-store URL must land in the Play Store / App Store app, not in a Custom Tab showing
   * the store's web page. Ignored on web, where every link is a new tab either way.
   */
  useSystemBrowser?: boolean;
}

/**
 * Open an external link, normalising it first and telling the user when nothing happened.
 *
 * Must stay callable straight from a click handler with no preceding await on web: the pop-up
 * blocker only honours `window.open` while the user-gesture call stack is still synchronous.
 */
export async function openExternalLink(
  url: string | null | undefined,
  options: ExternalLinkOptions = {},
): Promise<void> {
  const safeUrl = normalizeExternalUrl(url);
  if (!safeUrl) {
    await showCustomMessage({ messageText: MISSING_LINK_MESSAGE, messageType: "danger" });
    return;
  }

  const isNativePlatform = Capacitor.isNativePlatform();
  if (isNativePlatform) {
    if (options.useSystemBrowser) {
      try {
        const { completed } = await AppLauncher.openUrl({ url: safeUrl });
        if (completed) {
          return;
        }
      } catch (error) {
        console.error("System browser hand-off failed, falling back to the in-app browser:", error);
      }
    }
    try {
      await Browser.open({ url: safeUrl });
      return;
    } catch (error) {
      console.error("In-app browser hand-off failed, falling back to window.open:", error);
    }
  }

  const openedWindow = window.open(safeUrl, "_blank");
  if (!openedWindow) {
    // On device this is the last resort after both plugin hand-offs failed, so the pop-up advice
    // would be misleading there.
    await showCustomMessage({
      messageText: isNativePlatform ? UNOPENABLE_LINK_MESSAGE : BLOCKED_LINK_MESSAGE,
      messageType: "danger",
    });
    return;
  }
  // Unlike an <a target="_blank">, window.open still hands the opener reference to the new page.
  // Best-effort only: a browser may refuse the cross-origin write once the tab has navigated, and
  // that must not turn a link that opened fine into an error.
  try {
    openedWindow.opener = null;
  } catch (error) {
    console.warn("Could not detach the opener reference from the new tab:", error);
  }
}
